import { useState } from "react";
import { useAxios } from "../../../utils/useAxios";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";

/**
 * @typedef {Object} QuestAssignment
 * @property {number} id - The ID of the quest
 * @property {number} [order_index] - Optional order index for the quest
 * @property {boolean} is_assign - True when we want to assign and false when we want to unassign
 */

/**
 * @typedef {Object} AssignQuestsData
 * @property {QuestAssignment[]} quests - Array of quest assignments
 */

/**
 * Custom hook to handle assigning quests to a lesson
 */
export const useAssignQuestToLesson = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { axiosInstance } = useAxios();

  /**
   * Assigns quests to a lesson
   * @param {Object} params
   * @param {number} params.lessonId - The lesson ID
   * @param {AssignQuestsData} params.questsData - The quests data { quests: [{ id, order_index? }] }
   * @returns {Promise<Object|null>} The response data or null on error
   */
  const assignQuestToLesson = async ({ lessonId, questsData }) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.post(
        `${TENANT_DOMAIN}/lessons/${lessonId}/assign-quests`,
        { quests: questsData },
      );

      return response.data;
    } catch (err) {
      if (err?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    assignQuestToLesson,
    loading,
    error,
    setError
  };
};
